/**
 *  [2024] Zettant Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Zettant Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Zettant Incorporated and its
 * suppliers and may be covered by Japan. and Foreign Patents,
 * patents in process, and are protected by trade secret or
 * copyright law. Dissemination of this information or
 * reproduction of this material is strictly forbidden unless
 * prior written permission is obtained from Zettant Incorporated.
 */

/**
 * :ZNOTE:
 * tag: cacheManage, localStorage
 * note:
 *  - cacheManageをIndexedDBではなくlocalStorageにキャッシュするようにする
 *  - dexieと同じようなメソッドを提供する（cacheManageで使っている機能にだけ対応）
 * sample:
 *  - ほぼ全てのプロジェクト
 * :ZNOTE:
 */

import {Dexie} from 'dexie';

const LOCAL_STORAGE_LIMIT = 9*1024*1024;  // 9MB


interface IDataInStorage {
  [primaryKey: string]: any[];
}

/**
 * Dexieで提供されているbetween()に対するメソッドチェーンを模擬するためのクラス
 */
class DataProcessor {
  private key: string = '';  // primaryKeyのみ
  private keys: string[] = [];  // primaryKey, secondaryKey
  private data: any;

  constructor(keys: string|[string, string], data: any) {
    if (Array.isArray(keys)) {
      this.keys = keys;
    } else {
      this.key = keys;
    }
    this.data = data;
  }

  public between = (from: [string|number, string|number], to: [string|number, string|number], includeLower=true, includeUpper=true) => {
    // 初めてのchain reactionなら、dataがArrayがまだ配列になっていないので、value部分だけを取り出す
    if (!Array.isArray(this.data)) {
      this.data = this.data[from[0]];
      if (!this.data) this.data = [];
    }
    if (this.data.length === 0) return this;

    if (this.key) {
      this.data = this.data.filter((d) => d[this.key] >= from[0] && d[this.key] <= to[0]);
    } else {
      if (from[1] === Dexie.minKey && to[1] === Dexie.maxKey) {
        this.data = this.data.filter((d) => d[this.keys[0]] === from[0]);
      } else {
        // TODO: ここはちゃんと実装しなければならない (includeLower, includeUpperも加味する）
        this.data = this.data.filter((d) => d[this.keys[0]] >= from[0] && d[this.keys[0]] <= to[0] && d[this.keys[1]] >= from[1] && d[this.keys[1]] <= to[1]);
      }
    }
    return this;
  };

  public below = (value: string|number) => {
    if (!Array.isArray(this.data)) {
      if (this.key) {
        this.data = this.data[this.key];
      } else {
        this.data = this.data[this.keys[1]];
      }
      if (!this.data) this.data = [];
    }
    if (this.data.length === 0) return this;

    if (this.key) {
      this.data = this.data.filter((d) => d[this.key] < value);
    } else {
      this.data = this.data.filter((d) => d[this.keys[1]] < value);
    }
    return this;
  };

  public toArray = () => {
    if (Array.isArray(this.data)) {
      return this.data;
    }

    // whereの後に直接toArrayを呼ばれた場合は、全部配列にして返す
    return Object.values(this.data).flat(1);
  };
}


/**
 * localStorageに対するアクセスを、Dexieと同じメソッドで行えるようにするためのラッパークラス
 */
class LocalStorageAdapter {
  private keyName: string;
  private primaryKeyName: string;
  private secondaryKeyName = '';
  private manager: StorageManager;
  private noCacheMode = false;  // bulkPutなどで、いきなりサイズオーバーしてしまうような場合は、このフラグを立てることで、キャッシュの仕組みを使わないようにする
  private data: IDataInStorage = {};  // noCacheModeの時にだけ使う
  private clearCallback: () => void = () => {};

  constructor(table: string, topic: string, primaryKeyName: string, secondaryKeyName: string, sm: StorageManager) {
    this.keyName = `${table}###${topic}`;
    this.primaryKeyName = primaryKeyName;
    this.secondaryKeyName = secondaryKeyName;
    this.manager = sm;
    this.manager.set(this.keyName, localStorage.getItem(this.keyName));  // このアプリ全体で利用するローカルストレージサイズを管理し、超過しそうになったらローカルストレージをクリアできるようにする
    console.log('LocalStorage:', this.manager.get(this.keyName), 'bytes');
  }

  private getData(): IDataInStorage {
    if (this.noCacheMode) return this.data;

    const res = localStorage.getItem(this.keyName);
    if (!res || res === '[]' || res === '{}') return {};
    return JSON.parse(res) as IDataInStorage;
  }

  public toArray(): any[] {
    const res = this.getData();
    return Object.values(res).flat(1);
  }

  public where(condition: any) {
    let data: IDataInStorage = !this.noCacheMode ? this.getData() : this.data;
    if (!data) data = {};
    return new DataProcessor(condition, data);
  }

  public get(key: any) {
    const data: IDataInStorage = !this.noCacheMode ? this.getData() : this.data;
    if (!data) return;

    let res: any;
    if (this.secondaryKeyName) {
      if (!data[key[0]]) data[key[0]] = [];
      res = data[key[0]].filter((d) => d[this.primaryKeyName] === key[0] && d[this.secondaryKeyName] === key[1]);
    } else {
      if (!data[key]) data[key] = [];
      res = data[key].filter((d) => d[this.primaryKeyName] === key);
    }
    if (!res || res.length === 0) return;
    return res[0];
  }

  public put(record: any) {
    let data: IDataInStorage = !this.noCacheMode ? this.getData() : this.data;
    if (!data) data = {};
    if (!data[record[this.primaryKeyName]]) data[record[this.primaryKeyName]] = [];

    let idx: number;
    if (this.secondaryKeyName) {
      idx = data[record[this.primaryKeyName]].findIndex((d) => d[this.primaryKeyName] === record[this.primaryKeyName] && d[this.secondaryKeyName] === record[this.secondaryKeyName]);
    } else {
      idx = data[record[this.primaryKeyName]].findIndex((d) => d[this.primaryKeyName] === record[this.primaryKeyName]);
    }
    if (idx === -1) {
      data[record[this.primaryKeyName]].push(record);
    } else {
      data[record[this.primaryKeyName]][idx] = record;
    }

    const x = JSON.stringify(data);
    if (!this.noCacheMode && this.manager.shouldNoCache(this.keyName, x)) {
      this.clear();
      this.noCacheMode = true;
    }

    if (this.noCacheMode) {
      this.data = data;
    } else {
      // 容量オーバーしそうな時は、topic=entriesのいくつかのprimaryKeyを削除する（metaのlatestも削除する）
      storageOverflowCheck(this.keyName, x);
      localStorage.setItem(this.keyName, x);
    }
  }

  public bulkPut(records: any[]) {
    for (let i=1; i<records.length; i++) {
      if (records[0][this.primaryKeyName] !== records[i][this.primaryKeyName]) {
        throw new Error('bulkPut()に渡された全てのrecordsは同一のprimaryKey値を持たなければなりません');
      }
    }
    const data: IDataInStorage = !this.noCacheMode ? this.getData() : this.data;
    if (!data[records[0][this.primaryKeyName]]) data[records[0][this.primaryKeyName]] = [];

    for (const r of records) {
      let idx: number;
      if (this.secondaryKeyName) {
        idx = data[r[this.primaryKeyName]].findIndex((d) => d[this.primaryKeyName] === r[this.primaryKeyName] && d[this.secondaryKeyName] === r[this.secondaryKeyName]);
      } else {
        idx = data[r[this.primaryKeyName]].findIndex((d) => d[this.primaryKeyName] === r[this.primaryKeyName]);
      }
      if (idx === -1) {
        data[r[this.primaryKeyName]].push(r);
      } else {
        data[r[this.primaryKeyName]][idx] = r;
      }
    }
    const x = JSON.stringify(data);
    if (!this.noCacheMode && this.manager.shouldNoCache(this.keyName, x)) {
      this.clear();
      this.noCacheMode = true;
    }

    if (this.noCacheMode) {
      this.data = data;
    } else {
      // 容量オーバーしそうな時は、topic=entriesのいくつかのprimaryKeyを削除する（metaのlatestも削除する）
      storageOverflowCheck(this.keyName, x);
      localStorage.setItem(this.keyName, x);
    }
  }

  public delete(record: any) {
    const data: IDataInStorage = !this.noCacheMode ? this.getData() : this.data;
    if (!data || !data[record[this.primaryKeyName]]) return;
    let idx: number;
    if (this.secondaryKeyName) {
      idx = data[record[this.primaryKeyName]].findIndex((d) => d[this.primaryKeyName] === record[this.primaryKeyName] && d[this.secondaryKeyName] === record[this.secondaryKeyName]);
    } else {
      idx = data[record[this.primaryKeyName]].findIndex((d) => d[this.primaryKeyName] === record[this.primaryKeyName]);
    }
    if (idx === -1) return;
    data[record[this.primaryKeyName]].splice(idx, 1);

    if (this.noCacheMode) {
      this.data = data;
    } else {
      localStorage.setItem(this.keyName, JSON.stringify(data));
    }
  }

  /**
   * まとめて削除する
   * @param records {[string, string][]}  [primaryKeyValue, secondaryKeyValue]の配列
   */
  public bulkDelete(records: [string, string][]) {
    if (!records || records.length === 0) return;
    for (let i=1; i<records.length; i++) {
      if (records[0][0] !== records[i][0]) {
        throw new Error('bulkDelete()に渡された全てのrecordsは同一のprimaryKey値を持たなければなりません');
      }
    }
    const data: IDataInStorage = !this.noCacheMode ? this.getData() : this.data;
    const targetPrimaryKey = records[0][0];
    if (!data || !data[targetPrimaryKey]) return;

    for (const r of records) {
      let idx: number;
      if (this.secondaryKeyName) {
        idx = data[targetPrimaryKey].findIndex((d) => d[this.primaryKeyName] === targetPrimaryKey && d[this.secondaryKeyName] === r[1]);
      } else {
        idx = data[targetPrimaryKey].findIndex((d) => d[this.primaryKeyName] === targetPrimaryKey);
      }
      if (idx === -1) continue;
      data[targetPrimaryKey].splice(idx, 1);
    }

    if (this.noCacheMode) {
      this.data = data;
    } else {
      localStorage.setItem(this.keyName, JSON.stringify(data));
    }
  }

  public clear() {
    localStorage.setItem(this.keyName, JSON.stringify([]));
    this.noCacheMode = false;  // キャッシュクリアしたらとりあえず一度モードを戻してみる
    this.clearCallback();
    console.log('LocalStorage:', this.keyName, '[cleared]');
  }

  // このLocalStorageAdapterにだけ定義するメソッド（Dexieにはない）
  public setClearCallback = (callback: () => void) => this.clearCallback = callback;
}


/**
 * LocalStorageの使用量をチェックする（このLocalStorageAdapterだけを対象にする）
 */
class StorageManager {
  private topics: {[topic: string]: number} = {};
  private max: number;

  constructor(max: number) {
    this.max = max;  // デフォルトは9MB
  }

  public set(topic: string, content: string|undefined|null) {
    if (content) {
      this.topics[topic] = content.length;
    } else {
      this.topics[topic] = 0;
    }
  }

  public get(topic: string) {
    return this.topics[topic];
  }

  /**
   * いきなりLocalStorageの使用量をオーバーしてしまうような場合はtrueを返す
   * @param topic
   * @param content
   */
  public shouldNoCache(topic: string, content: string) {
    return content.length > Math.floor(this.max / 4);  // 一発で1/4以上使うような場合は、キャッシュがまともに働かない可能性が高いので、noCacheModeにする
  }

  /**
   * これを加えると上限を超えてしまうかどうかをチェックする
   * @param topic {string}
   * @param size {number}
   */
  public willFull(topic: string, size: number) {
    const sz = Object.keys(this.topics).filter((t) => t !== topic).reduce((a, b) => a + this.topics[b], 0);
    return sz + size > this.max;
  }
}

/**
 * これを加えると上限を超えてしまうかどうかをチェックする。超えてしまうなら、キャッシュクリアする
 * @param keyName
 * @param additionalContent
 */
const storageOverflowCheck = (keyName: string, additionalContent: string) => {
  if (!storageManager.willFull(keyName, additionalContent.length)) return;

  // このままだとオーバーするので、キャッシュを強制的にクリアする
  for (const topic in localStorageAdapters) {
    localStorageAdapters[topic].clear();
  }
};


const storageManager = new StorageManager(LOCAL_STORAGE_LIMIT);

const localStorageAdapters: {[name:string]: LocalStorageAdapter} = {};
const newLocalStorageAdapter = (table: string, topic: string, primaryKeyName: string, secondaryKeyName='') => {
  localStorageAdapters[topic] = new LocalStorageAdapter(table, topic, primaryKeyName, secondaryKeyName, storageManager);
  return localStorageAdapters[topic];
};

export {
  LocalStorageAdapter, newLocalStorageAdapter
};